import React, { useState, useMemo, useCallback, useEffect, Component } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Tabs,
  Tab,
  Container,
  Paper,
  Alert,
} from '@mui/material';
import { Logout as LogoutIcon } from '@mui/icons-material';
import { PatientInquiryList } from './PatientInquiryList';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { ModerationQueue } from './ModerationQueue';
import UserReports from './UserReports';
import { AccountDeletionList } from './AccountDeletionList';
import { ComplianceReviewTab } from './ComplianceReviewTab';
import { NewsletterTab } from './NewsletterTab';
import { AdminAccessTab } from './AdminAccessTab';
import { CustomBarcodesTab } from './CustomBarcodesTab';
import { SignupsTab } from './SignupsTab';
import { RecipeManagerTab } from './RecipeManagerTab';
import { ChallengesTab } from './ChallengesTab';
import { PushNotificationsTab } from './PushNotificationsTab';
import { FeedbackTab } from './FeedbackTab';
import { ICD10CoderTab } from './ICD10CoderTab';
import { EducationTab } from './EducationTab';
import { AdminPermissions } from '../authConfig';

// ── Error Boundary ────────────────────────────────────────────────────────────
interface EBState { error: Error | null; info: string }
class TabErrorBoundary extends Component<{ children: React.ReactNode; tabLabel: string }, EBState> {
  constructor(props: any) {
    super(props);
    this.state = { error: null, info: '' };
  }
  static getDerivedStateFromError(error: Error): EBState {
    return { error, info: '' };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[TabErrorBoundary] Crash in tab "${this.props.tabLabel}":`, error, info.componentStack);
    this.setState({ info: info.componentStack ?? '' });
  }
  render() {
    if (this.state.error) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>This tab crashed:</strong> {this.state.error.message}
          </Alert>
          <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary', fontSize: 11 }}>
            {this.state.info}
          </Typography>
          <Button sx={{ mt: 2 }} variant="outlined" onClick={() => this.setState({ error: null, info: '' })}>
            Retry
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

interface AdminDashboardProps {
  onLogout: () => void;
  userEmail?: string;
  permissions?: AdminPermissions;
  displayName?: string;
}

// Tab configuration - maps permission keys to tab components
interface TabConfig {
  id: string;
  label: string;
  permissionKey: keyof AdminPermissions;
  component: React.ReactNode;
}

function buildTabs(canViewInsuranceCards: boolean): TabConfig[] {
  return [
  { id: 'patient_inquiries', label: '📝 Patient Inquiries', permissionKey: 'patient_inquiries', component: <PatientInquiryList canViewInsuranceCards={canViewInsuranceCards} /> },
  { id: 'analytics', label: '📊 Analytics', permissionKey: 'analytics', component: <AnalyticsDashboard /> },
  { id: 'moderation', label: '🔍 Moderation', permissionKey: 'moderation', component: <ModerationQueue /> },
  { id: 'user_reports', label: '🚩 User Reports', permissionKey: 'user_reports', component: <UserReports /> },
  { id: 'account_deletions', label: '🗑️ Account Deletions', permissionKey: 'account_deletions', component: <AccountDeletionList /> },
  { id: 'compliance_review', label: '🔬 Compliance Review', permissionKey: 'compliance_review', component: <ComplianceReviewTab /> },
  { id: 'newsletter', label: '✉️ Newsletter', permissionKey: 'newsletter', component: <NewsletterTab /> },
  { id: 'custom_barcodes', label: '📦 Custom Barcodes', permissionKey: 'custom_barcodes', component: <CustomBarcodesTab /> },
  { id: 'admin_access', label: '🔑 Admin Access', permissionKey: 'admin_access', component: <AdminAccessTab /> },
  { id: 'signups', label: '📋 Signups', permissionKey: 'signups', component: <SignupsTab /> },
  { id: 'recipe_manager', label: '🍳 Recipe Manager', permissionKey: 'recipe_manager', component: <RecipeManagerTab /> },
  { id: 'challenges', label: '🏆 Challenges', permissionKey: 'challenges', component: <ChallengesTab /> },
  { id: 'push_notifications', label: '🔔 Push Notifications', permissionKey: 'push_notifications', component: <PushNotificationsTab /> },
  { id: 'feedback', label: '💬 Feedback', permissionKey: 'feedback', component: <FeedbackTab /> },
  { id: 'icd10_coder', label: '🏥 ICD-10 Coder', permissionKey: 'icd10_coder', component: <ICD10CoderTab /> },
  { id: 'education', label: '📚 Education', permissionKey: 'education', component: <EducationTab /> },
  ];
}

// localStorage keys for persisting UI preferences across refreshes
const TAB_ORDER_KEY = 'famelia_admin_tab_order';
const ACTIVE_TAB_KEY = 'famelia_admin_active_tab';

function loadStoredOrder(): string[] {
  try {
    const raw = localStorage.getItem(TAB_ORDER_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export function AdminDashboard({ onLogout, userEmail, permissions, displayName: propDisplayName }: AdminDashboardProps) {
  // Persisted custom tab order (array of tab ids) and last-open tab id
  const [tabOrder, setTabOrder] = useState<string[]>(() => loadStoredOrder());
  const [activeTabId, setActiveTabId] = useState<string | null>(() => localStorage.getItem(ACTIVE_TAB_KEY));
  const [draggingId, setDraggingId] = useState<string | null>(null);

  // Tabs this admin is allowed to see (from Supabase permissions)
  const permittedTabs = useMemo(() => {
    if (!permissions) return [];
    return buildTabs(!!permissions.insurance_cards).filter(tab => permissions[tab.permissionKey]);
  }, [permissions]);

  // Apply the persisted custom order; any tab not yet in the saved order
  // (e.g. a newly added tab) is appended at the end in its default position.
  const visibleTabs = useMemo(() => {
    if (permittedTabs.length === 0) return [];
    const byId = new Map(permittedTabs.map(t => [t.id, t]));
    const ordered: TabConfig[] = [];
    for (const id of tabOrder) {
      const t = byId.get(id);
      if (t) { ordered.push(t); byId.delete(id); }
    }
    // Remaining (unseen) tabs keep their default relative order
    for (const t of permittedTabs) {
      if (byId.has(t.id)) ordered.push(t);
    }
    return ordered;
  }, [permittedTabs, tabOrder]);

  // Resolve the active index from the persisted id (falls back to first tab)
  const activeTab = useMemo(() => {
    const idx = activeTabId ? visibleTabs.findIndex(t => t.id === activeTabId) : -1;
    return idx >= 0 ? idx : 0;
  }, [activeTabId, visibleTabs]);

  const displayName = propDisplayName || userEmail || 'Admin';

  // Handle tab change - persist the selected tab id so a refresh restores it
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const tab = visibleTabs[newValue];
    if (tab) {
      setActiveTabId(tab.id);
      try { localStorage.setItem(ACTIVE_TAB_KEY, tab.id); } catch { /* ignore */ }
    }
  };

  // Keep a valid active tab persisted once tabs are known (first load / cleanup)
  useEffect(() => {
    if (visibleTabs.length === 0) return;
    const current = activeTabId && visibleTabs.some(t => t.id === activeTabId)
      ? activeTabId
      : visibleTabs[0].id;
    if (current !== activeTabId) {
      setActiveTabId(current);
      try { localStorage.setItem(ACTIVE_TAB_KEY, current); } catch { /* ignore */ }
    }
  }, [visibleTabs, activeTabId]);

  // ── Drag-and-drop reordering ────────────────────────────────────────────────
  const persistOrder = useCallback((tabs: TabConfig[]) => {
    const ids = tabs.map(t => t.id);
    setTabOrder(ids);
    try { localStorage.setItem(TAB_ORDER_KEY, JSON.stringify(ids)); } catch { /* ignore */ }
  }, []);

  const handleDragStart = (id: string) => (e: React.DragEvent) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', id); } catch { /* Safari */ }
  };

  const handleDragOver = (overId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!draggingId || draggingId === overId) return;
    const from = visibleTabs.findIndex(t => t.id === draggingId);
    const to = visibleTabs.findIndex(t => t.id === overId);
    if (from === -1 || to === -1) return;
    const next = [...visibleTabs];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    persistOrder(next);
  };

  const handleDragEnd = () => setDraggingId(null);

  // Get the current tab's component
  const currentTab = visibleTabs[activeTab];

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: '#fafafa' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            <span style={{ fontFamily: 'Lobster, cursive', fontSize: '1.4rem' }}>&nbsp;famēlia</span>
            <span style={{ fontWeight: 400, fontSize: '0.85rem', opacity: 0.85, marginLeft: 6 }}>Admin</span>
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, opacity: 0.85 }}>
            {displayName}
          </Typography>
          <Button
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={onLogout}
            sx={{ borderRadius: '20px', textTransform: 'none', fontWeight: 600 }}
          >
            Logout
          </Button>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="inherit"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            bgcolor: 'primary.main',
            px: 1,
            pb: 1,
            '& .MuiTab-root': {
              borderRadius: '20px',
              mx: '3px',
              px: '16px',
              minHeight: '36px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'rgba(255,255,255,0.75)',
              transition: 'background-color 0.2s, color 0.2s',
            },
            '& .Mui-selected': {
              bgcolor: 'rgba(255,255,255,0.22)',
              color: '#ffffff !important',
            },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.id}
              label={tab.label}
              draggable
              onDragStart={handleDragStart(tab.id)}
              onDragOver={handleDragOver(tab.id)}
              onDrop={(e: React.DragEvent) => e.preventDefault()}
              onDragEnd={handleDragEnd}
              title="Drag to reorder"
              sx={{
                cursor: 'grab',
                opacity: draggingId === tab.id ? 0.5 : 1,
                '&:active': { cursor: 'grabbing' },
              }}
            />
          ))}
        </Tabs>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={1} sx={{ p: 3, minHeight: '70vh', borderRadius: 3 }}>
          {currentTab ? (
            <TabErrorBoundary key={currentTab.id} tabLabel={currentTab.label}>
              {currentTab.component}
            </TabErrorBoundary>
          ) : (
            <Typography color="text.secondary">
              No tabs available. Please contact an administrator.
            </Typography>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
